import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useApiClient } from "@/hooks/useApiClient";
import { handlePropertyCodeApiError } from "@/features/propertyCodeAcquisition/utils/handlePropertyCodeApiError";
import { postBulkPictureRequest } from "@/features/propertyCodeBulkAcquisition/api/postBulkPictureRequest";
import type { BulkAcquisitionRequest } from "@/features/propertyCodeBulkAcquisition/types";

interface UseBulkAcquireSubmitReturn {
  /**
   * 一括依頼を送信する。受付成功（201）時は取得済み一覧へ遷移する。
   * 失敗時は cancelLoading で再実行可能に戻す
   */
  submit: (
    request: BulkAcquisitionRequest,
    cancelLoading: () => void
  ) => Promise<void>;
}

/** 一括取得依頼の POST。エラー分岐は同じ DataLens 連携の handlePropertyCodeApiError に委ねる */
export const useBulkAcquireSubmit = (): UseBulkAcquireSubmitReturn => {
  const { apiClient } = useApiClient();
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);

  const submit = async (
    request: BulkAcquisitionRequest,
    cancelLoading: () => void
  ): Promise<void> => {
    // タイムアウト後もリクエストは生きているため、送信中の再クリックによる二重依頼を防ぐ
    if (isSubmittingRef.current) {
      cancelLoading();
      return;
    }
    isSubmittingRef.current = true;
    try {
      const response = await postBulkPictureRequest(apiClient, request);
      // 解析リクエスト作成のみ失敗しても依頼本体は成立している（201）。
      // 再送信は二重課金になるため、エラーではなく受付済みの警告として通知する
      const failedAnalyzeNames = [
        ...(response.ownerAnalyzeRequestFailed ? ["所有者事項"] : []),
        ...(response.allMattersAnalyzeRequestFailed ? ["全部事項"] : []),
      ];
      if (failedAnalyzeNames.length > 0) {
        toast.warning(
          `取得依頼は受け付けましたが、${failedAnalyzeNames.join(
            "・"
          )}の解析の依頼に失敗しました。取得完了後に取得済不動産登記の詳細を確認する画面の操作メニューから解析を実行してください`
        );
      }
      navigate("/acquirebooklist");
    } catch (e) {
      handlePropertyCodeApiError(e, {
        fallbackMessage: "予期しないエラーが発生しました",
        handleBadRequest: true,
        handleFieldErrors: true,
      });
      cancelLoading();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return { submit };
};
